import React, {
  type ComponentPropsWithoutRef,
  forwardRef,
  type ReactNode,
  type CSSProperties,
} from 'react';

import { cn } from '../../lib/utils';
import { Input } from '../ui/input2';
import { Text } from '../common/Text';
import { Toggle } from '../ui/toggle';

type FieldLabelProps = {
  title: string;
  flush?: boolean;
  style?: CSSProperties;
};

export function FieldLabel({ title, flush, style }: FieldLabelProps) {
  return (
    <Text
      style={{
        marginBottom: 5,
        marginTop: flush ? 0 : 25,
        fontSize: 14,
        color: 'var(--color-tableRowHeaderText)',
        padding: `0 ${12}px`,
        userSelect: 'none',
        ...style,
      }}
    >
      {title}
    </Text>
  );
}

const valueStyle = {
  borderWidth: 1,
  borderColor: 'var(--color-formInputBorder)',
  marginLeft: 8,
  marginRight: 8,
  height: 40,
};

type InputFieldProps = ComponentPropsWithRef<typeof Input>;

export const InputField = forwardRef<HTMLInputElement, InputFieldProps>(
  ({ disabled, style, onUpdate, ...props }, ref) => {
    return (
      <Input
        inputRef={ref}
        autoCorrect="false"
        autoCapitalize="none"
        disabled={disabled}
        onUpdate={onUpdate}
        style={{
          ...valueStyle,
          ...style,
          color: disabled ? 'var(--color-tableTextInactive)' : 'var(--color-tableText)',
          backgroundColor: disabled
            ? 'var(--color-formInputTextReadOnlySelection)'
            : 'var(--color-tableBackground)',
        }}
        {...props}
      />
    );
  },
);

InputField.displayName = 'InputField';

type TapFieldProps = React.ComponentPropsWithRef<'div'> & {
  value?: string | number;
  textStyle?: CSSProperties;
  rightContent?: ReactNode;
  disabled?: boolean;
};

export const TapField = forwardRef<HTMLDivElement, TapFieldProps>(
  (
    {
      value,
      children,
      disabled,
      rightContent,
      style,
      textStyle,
      onClick,
      className,
      ...props
    },
    ref,
  ) => {
    return (
      <div
        ref={ref}
        role="button"
        tabIndex={disabled ? undefined : 0}
        onClick={!disabled ? onClick : undefined}
        className={cn('active:opacity-50', className)}
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          ...style,
          ...valueStyle,
          backgroundColor: 'var(--color-tableBackground)',
          ...(disabled && {
            backgroundColor: 'var(--color-formInputTextReadOnlySelection)',
          }),
        }}
        {...props}
      >
        {children ? (
          children
        ) : (
          <Text style={{ flex: 1, userSelect: 'none', ...textStyle }}>
            {value}
          </Text>
        )}
        {!disabled && rightContent}
      </div>
    );
  },
);

TapField.displayName = 'TapField';

type ToggleFieldProps = ComponentPropsWithoutRef<typeof Toggle>;

export function ToggleField({
  id,
  isOn,
  onToggle,
  style,
  className,
  isDisabled = false,
}: ToggleFieldProps) {
  return (
    <Toggle
      id={id}
      isOn={isOn}
      isDisabled={isDisabled}
      onToggle={onToggle}
      style={style}
      className={cn(
        '[&_[data-toggle-container]]:w-[50px] [&_[data-toggle-container]]:h-6',
        '[&_[data-toggle]]:w-5 [&_[data-toggle]]:h-5',
        className,
      )}
    />
  );
}
